// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { useCurrentUser } from '@Queries/userQueries';
import { computeEventList as computeUserEventList } from '@Organisms/User/eventList';
import { computeEventList as computeAuthEventList } from '@Organisms/AccountDetails/eventList';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import client from '@Client';
import useValidateForm from '@Hooks/useValidateForm';
import { useGenericQuery } from '@Queries/genericQueries';
import { PasswordPolicyOutput } from 'thehive-sdk';
import { computeValidatePassword } from '@Helpers/password';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { RulesViewerPassword } from '@Molecules/RulesPassword/RulesPassword';
import usePageTitle from '@Hooks/usePageTitle';

import './Password.scss';

interface EditPasswordFormValues {
    currentPassword: string;
    newPassword: string;
}

const Password = (): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange } = useValidateForm();
    const [fieldsTouched, setFieldsTouched] = useState(false);

    const { data: currentUser } = useCurrentUser();
    const { _id: userId = '' } = currentUser || {};

    const userEventList = useMemo(
        () =>
            computeUserEventList({
                controller: client.UserController,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
                locale: i,
            }),
        [i],
    );

    const authEventList = useMemo(
        () =>
            computeAuthEventList({
                controller: client.AuthController,
                onSuccess: (count, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
                locale: i,
            }),
        [i],
    );

    const { data: passwordPolicy } = useGenericQuery<PasswordPolicyOutput>({
        fetchCallback: () => authEventList.onGetPasswordPolicy(),
        key: ['get-password-policy'],
        manual: true,
    });

    const validator = useCallback(
        (str) => computeValidatePassword(passwordPolicy as PasswordPolicyOutput)(str),
        [passwordPolicy],
    );

    const onFinish = ({ currentPassword, newPassword }: EditPasswordFormValues) =>
        userEventList
            .onChangePassword(currentPassword, newPassword, userId)
            .then(() => {
                openMessage('success', i('notification.password.edit'));
            })
            .catch(() => openMessage('error', i('notification.password.edit-failed')))
            .finally(() => {
                form.resetFields();
                setFieldsTouched(false);
            });

    usePageTitle(i('page-title.account.password'));

    return (
        <THLayout>
            <THLayout.Content>
                <THForm
                    name="edit-password-form"
                    form={form}
                    onFinish={onFinish}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                warning
                                onCancel={() => {
                                    setFieldsTouched(false);
                                    form.resetFields();
                                }}
                                disabled={!isValid}
                                hidden={!fieldsTouched}
                            />
                        }
                    >
                        <THForm.Item name="currentPassword" label={i('account.passwordCurrent')}>
                            <THPassword type="password" placeholder={i('account.passwordCurrent')} />
                        </THForm.Item>

                        <THForm.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    name="newPassword"
                                    label={i('account.password')}
                                    help={
                                        passwordPolicy?.enabled && (
                                            <RulesViewerPassword
                                                local={i}
                                                rules={passwordPolicy}
                                                error={validator(getFieldValue('newPassword') || '')}
                                                active
                                            />
                                        )
                                    }
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                const validate = validator(value);
                                                if (
                                                    passwordPolicy?.enabled &&
                                                    ((Array.isArray(validate) && validate.length) ||
                                                        (passwordPolicy.cannotContainUsername &&
                                                            value === currentUser?.login))
                                                ) {
                                                    return Promise.reject(validate);
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <THPassword type="password" placeholder={i('account.password')} />
                                </THForm.Item>
                            )}
                        </THForm.Item>

                        <THForm.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    name="confirmPassword"
                                    label={i('account.passwordConfirm')}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (value !== getFieldValue('newPassword') || !value) {
                                                    return Promise.reject();
                                                }

                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <THPassword placeholder={i('account.passwordConfirm')} />
                                </THForm.Item>
                            )}
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'Password',
    },
    path: RouteBuilder.compute('account.password'),
    content: <Password />,
    space: ['admin', 'user'],
    breadcrumb: { label: 'Password' },
};
